import { api } from '../api';
import { state, isAdmin } from '../app';
import { esc, $, $$, setHTML, formatCurrency, statusBadge, timeAgo, greeting, icons } from '../helpers';
import { toast, setBreadcrumb, skeletonStats } from '../ui';
import { renderRevenueChart } from '../chart-helper';

export async function renderDashboard() {
  const content = $('#content')!;
  setBreadcrumb([{ label: 'Dashboard' }]);
  // SAFE: skeletonStats returns only static HTML
  setHTML(content, skeletonStats(4));

  try {
    const data = await api.get('/dashboard');

    if (isAdmin()) {
      const projectStats: Record<string, number> = {};
      (data.stats.projects || []).forEach((p: any) => { projectStats[p.status] = p.count; });
      const totalProjects = Object.values(projectStats).reduce((a: number, b: number) => a + b, 0);
      const activeProjects = (projectStats.active || 0) + (projectStats.in_progress || 0);

      let activityHtml = '';
      if (!(data.recent_activity || []).length) {
        activityHtml = '<p style="color:var(--text-muted)">No recent activity</p>';
      } else {
        const actionLabels: Record<string, { label: string; icon: string }> = {
          login: { label: 'Logged in', icon: 'log-in' },
          login_failed: { label: 'Failed login', icon: 'shield-alert' },
          created: { label: 'Created', icon: 'plus-circle' },
          updated: { label: 'Updated', icon: 'edit' },
          deleted: { label: 'Deleted', icon: 'trash-2' },
          sent: { label: 'Sent', icon: 'send' },
          approved: { label: 'Approved', icon: 'check-circle' },
          rejected: { label: 'Rejected', icon: 'x-circle' },
          confirmed: { label: 'Confirmed', icon: 'check-circle' },
          password_changed: { label: 'Password changed', icon: 'key' },
          profile_updated: { label: 'Profile updated', icon: 'user' },
        };
        data.recent_activity.slice(0, 10).forEach((a: any) => {
          const info = actionLabels[a.action] || { label: a.action, icon: 'activity' };
          const userName = esc(a.user_name || 'System');
          const countryTag = a.country_code ? ` <span style="font-size:0.7rem;background:var(--bg-primary);border:1px solid var(--border);border-radius:3px;padding:0 4px;color:var(--text-secondary)">${esc(a.country_code)}</span>` : '';
          const ipText = a.ip_address ? `<span class="font-mono" style="font-size:0.75rem;color:var(--text-muted);margin-left:0.3rem">${esc(a.ip_address)}${countryTag}</span>` : '';
          activityHtml += `<div class="activity-item"><div class="activity-icon"><i data-lucide="${info.icon}"></i></div><div class="activity-content"><div><strong>${userName}</strong> <span style="color:var(--text-secondary)">${esc(info.label).toLowerCase()}</span></div><div class="activity-time">${timeAgo(a.created_at)}${ipText}</div></div></div>`;
        });
      }

      // All dynamic values escaped via esc() / formatCurrency()
      setHTML(content, `<div class="page-enter">
        <div class="page-header"><h1>Dashboard</h1>
        <div class="page-header-actions">
          <a href="/clients" class="btn btn-secondary"><i data-lucide="user-plus"></i> New Client</a>
          <a href="/invoices" class="btn btn-primary"><i data-lucide="plus"></i> New Invoice</a>
        </div></div>
        <div class="stats-grid">
          <div class="stat-card"><div class="stat-label"><i data-lucide="users"></i> Total Clients</div><div class="stat-value">${esc(data.stats.total_clients)}</div></div>
          <div class="stat-card"><div class="stat-label"><i data-lucide="folder-kanban"></i> Active Projects</div><div class="stat-value">${esc(activeProjects)}</div><div class="stat-change">${esc(totalProjects)} total</div></div>
          <div class="stat-card"><div class="stat-label"><i data-lucide="circle-dollar-sign"></i> Total Revenue</div><div class="stat-value">${formatCurrency(data.stats.total_revenue)}</div></div>
          <div class="stat-card"><div class="stat-label"><i data-lucide="clock"></i> Pending Payments</div><div class="stat-value">${formatCurrency(data.stats.pending_payments)}</div></div>
        </div>
        ${data.stats.pending_confirmations > 0 ? `<div class="card mb-2" style="border-left:3px solid var(--warning)"><div class="card-header" style="display:flex;align-items:center;justify-content:space-between"><h3><i data-lucide="alert-circle" style="width:18px;height:18px;vertical-align:middle;margin-right:0.3rem;color:var(--warning)"></i> ${esc(data.stats.pending_confirmations)} payment${data.stats.pending_confirmations > 1 ? 's' : ''} awaiting confirmation</h3><a href="/payments" class="btn btn-primary btn-sm">Review</a></div><div class="card-body"><p style="color:var(--text-muted);margin:0">Clients have notified payment. Verify in your bank and confirm.</p></div></div>` : ''}
        ${data.stats.pending_approvals > 0 ? `<div class="card mb-2" style="border-left:3px solid var(--warning)"><div class="card-header" style="display:flex;align-items:center;justify-content:space-between"><h3><i data-lucide="user-check" style="width:18px;height:18px;vertical-align:middle;margin-right:0.3rem"></i> Pending Approvals (${esc(data.stats.pending_approvals)})</h3><a href="/clients?tab=pending" class="btn btn-primary btn-sm">Review</a></div><div class="card-body"><p style="color:var(--text-muted);margin:0">New registration${data.stats.pending_approvals > 1 ? 's' : ''} waiting for your approval.</p></div></div>` : ''}
        ${data.stats.pending_form8300 > 0 ? `<div class="card mb-2" style="border-left:3px solid ${data.stats.overdue_form8300 > 0 ? 'var(--danger)' : 'var(--warning)'}"><div class="card-header" style="display:flex;align-items:center;justify-content:space-between"><h3><i data-lucide="shield-alert" style="width:18px;height:18px;vertical-align:middle;margin-right:0.3rem;color:${data.stats.overdue_form8300 > 0 ? 'var(--danger)' : 'var(--warning)'}"></i> ${esc(data.stats.pending_form8300)} Form 8300 filing${data.stats.pending_form8300 > 1 ? 's' : ''} required${data.stats.overdue_form8300 > 0 ? ' (' + data.stats.overdue_form8300 + ' OVERDUE)' : ''}</h3><a href="/compliance" class="btn btn-primary btn-sm">Review</a></div><div class="card-body"><p style="color:var(--text-muted);margin:0">Federal 15-day filing deadline for $10,000+ digital asset receipts.</p></div></div>` : ''}
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:1rem;align-items:start">
          <div class="card"><div class="card-header"><h3>Monthly Revenue</h3></div><div class="card-body" style="height:280px"><canvas id="revenue-chart"></canvas><div id="revenue-empty" style="display:none;text-align:center;color:var(--text-muted);padding:2rem 0;font-size:var(--text-sm)">No revenue data yet</div></div></div>
          <div class="card"><div class="card-header"><h3>Recent Activity</h3></div><div class="card-body" style="max-height:300px;overflow-y:auto">${activityHtml}</div></div>
        </div>
      </div>`);

      icons();

      if (data.stats.monthly_revenue?.length) {
        renderRevenueChart('revenue-chart', data.stats.monthly_revenue);
      } else {
        const canvas = document.getElementById('revenue-chart') as HTMLCanvasElement | null;
        const emptyMsg = document.getElementById('revenue-empty');
        if (canvas) canvas.style.display = 'none';
        if (emptyMsg) emptyMsg.style.display = 'block';
      }

    } else {
      // Customer dashboard — CRO optimized
      const pStats: Record<string, number> = {};
      (data.stats.projects || []).forEach((p: any) => { pStats[p.status] = p.count; });
      const tp = Object.values(pStats).reduce((a: number, b: number) => a + b, 0);
      const iStats: Record<string, number> = {};
      let totalOwed = 0;
      (data.stats.invoices || []).forEach((i: any) => {
        iStats[i.status] = i.count;
        if (i.status === 'sent' || i.status === 'overdue') totalOwed += i.total;
      });

      const unpaid = data.stats.unpaid_invoices || [];
      const unpaidCount = (iStats.sent || 0) + (iStats.overdue || 0);
      const hasOverdue = unpaid.some((u: any) => u.status === 'overdue' || (u.due_date && new Date(u.due_date) < new Date()));

      // Unpaid invoices CTA banner
      let urgentBanner = '';
      if (unpaid.length > 0) {
        let invoiceRows = '';
        unpaid.forEach((u: any) => {
          const isOverdue = u.status === 'overdue' || (u.due_date && new Date(u.due_date) < new Date());
          const dueText = u.due_date
            ? (isOverdue
              ? '<span style="color:var(--danger);font-weight:600">Overdue</span>'
              : 'Due ' + new Date(u.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }))
            : '';
          invoiceRows += `<div style="display:flex;align-items:center;justify-content:space-between;padding:0.7rem 0;${u !== unpaid[unpaid.length - 1] ? 'border-bottom:1px solid rgba(255,255,255,0.15);' : ''}">
            <div>
              <span class="font-mono" style="font-weight:600">${esc(u.invoice_number)}</span>
              <span style="opacity:0.7;margin-left:0.5rem;font-size:0.85rem">${dueText}</span>
            </div>
            <div style="display:flex;align-items:center;gap:0.8rem">
              <span style="font-weight:700;font-size:1.05rem">${formatCurrency(u.total, u.currency)}</span>
              <a href="/invoices/${u.id}" class="btn" style="background:white;color:#1a1a1a;font-weight:600;padding:0.4rem 1rem;border-radius:8px;font-size:0.82rem;text-decoration:none">
                Pay Now
              </a>
            </div>
          </div>`;
        });

        const bannerColor = hasOverdue
          ? 'linear-gradient(135deg, #C0392B 0%, #E74C3C 100%)'
          : 'linear-gradient(135deg, #B8960C 0%, #D4AF37 100%)';
        const bannerIcon = hasOverdue ? 'alert-circle' : 'file-text';

        urgentBanner = `<div style="background:${bannerColor};border-radius:var(--radius-md);padding:1.2rem 1.5rem;color:white;margin-bottom:1rem;box-shadow:var(--shadow-md)">
          <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:0.6rem">
            <div style="display:flex;align-items:center;gap:0.5rem">
              <i data-lucide="${bannerIcon}" style="width:20px;height:20px"></i>
              <span style="font-weight:700;font-size:1rem">${hasOverdue ? 'You have overdue invoices' : unpaidCount + ' invoice' + (unpaidCount > 1 ? 's' : '') + ' waiting for payment'}</span>
            </div>
            <span style="font-size:1.3rem;font-weight:700">${formatCurrency(totalOwed)}</span>
          </div>
          ${invoiceRows}
        </div>`;
      }

      const firstName = esc(state.user!.name.split(' ')[0]);
      setHTML(content, `<div class="page-enter">
        <div class="page-header"><h1>${greeting()}, ${firstName}</h1></div>
        ${urgentBanner}
        <div class="stats-grid">
          <div class="stat-card${unpaidCount > 0 ? ' stat-card-highlight' : ''}">
            <div class="stat-label"><i data-lucide="file-text"></i> Unpaid Invoices</div>
            <div class="stat-value">${esc(unpaidCount)}</div>
            <div class="stat-change">${formatCurrency(totalOwed)} total</div>
            ${unpaidCount > 0 ? '<a href="/invoices" style="display:inline-block;margin-top:0.4rem;font-size:0.8rem;color:var(--accent);font-weight:600;text-decoration:none">View & Pay &rarr;</a>' : ''}
          </div>
          <div class="stat-card"><div class="stat-label"><i data-lucide="folder-kanban"></i> Your Projects</div><div class="stat-value">${esc(tp)}</div><div class="stat-change">${esc(pStats.in_progress || 0)} in progress</div></div>
          <div class="stat-card"><div class="stat-label"><i data-lucide="life-buoy"></i> Open Tickets</div><div class="stat-value">${esc(data.stats.open_tickets || 0)}</div></div>
        </div>
        <div class="card"><div class="card-header"><h3>Quick Actions</h3></div><div class="card-body" style="display:flex;gap:0.8rem;flex-wrap:wrap">
          ${unpaidCount > 0 ? '<a href="/invoices" class="btn btn-primary"><i data-lucide="wallet"></i> Pay Invoices</a>' : ''}
          <a href="/projects" class="btn btn-secondary"><i data-lucide="folder-kanban"></i> View Projects</a>
          <a href="/tickets" class="btn btn-secondary"><i data-lucide="life-buoy"></i> Support</a>
        </div></div>
      </div>`);
      icons();
    }
  } catch (err: any) {
    setHTML(content, `<div class="empty-state"><p>Failed to load dashboard: ${esc(err.message)}</p></div>`);
  }
}
